import './Input.scss';
import clsx from 'clsx';
import React, { memo, InputHTMLAttributes, DetailedHTMLProps, ReactNode, forwardRef, ForwardedRef } from 'react';
import { ErrorIcon } from 'src/modules/common/icons/ErrorIcon';

type InputAttributes = DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;

export type InputProps = Omit<InputAttributes, 'size' | 'ref'> & {
  readonly size?: 'lg' | 'md' | 'sm';
  readonly theme?: 'white' | 'gray';
  readonly label?: ReactNode;
  readonly icon?: ReactNode;
  readonly required?: boolean;
  readonly invalid?: boolean;
  readonly invalidText?: ReactNode;
  readonly description?: ReactNode;
  readonly actions?: ReactNode;
};

const InputWithoutMemo = forwardRef(({
  label,
  description,
  icon,
  invalid,
  invalidText,
  required,
  theme = 'gray',
  size = 'md',
  actions,
  ...props
}: InputProps, ref: ForwardedRef<HTMLInputElement>) => {
  const helperText = invalid ? invalidText || description : description;

  return (
    <div className="bp-input">
      {label && (
        <div className={clsx({
          'bp-input__label': true,
          'bp-input__label--disabled': props.disabled,
        })}
        >
          {label}

          {required && (
            <sup className={clsx({
              'bp-input__required': true,
              'bp-input__required--disabled': props.disabled,
            })}
            >*
            </sup>
          )}
        </div>
      )}

      <div className="bp-input__wrapper">
        {icon && (
          <div className="bp-input__icon">
            {icon}
          </div>
        )}

        <input
          {...props}
          ref={ref}
          className={clsx({
            'bp-input__input': true,
            'bp-input__input--readonly': props.readOnly,
            'bp-input__input--invalid': invalid,
            [`bp-input__input--${theme}`]: true,
            [`bp-input__input--${size}`]: true,
          })}
        />

        <div className={clsx({
          'bp-input__end-adornment': true,
          'bp-input__end-adornment--disabled': props.disabled,
        })}
        >
          {invalid && (
            <div className="bp-input__error-icon">
              <ErrorIcon/>
            </div>
          )}

          {actions}
        </div>
      </div>

      {helperText && (
        <div className={clsx({
          'bp-input__description': true,
          'bp-input__description--disabled': props.disabled,
          'bp-input__description--invalid': invalid,
        })}
        >
          {helperText}
        </div>
      )}
    </div>
  );
});

export const Input = memo(InputWithoutMemo);
